import axios from "axios";

// Unset in production: the backend serves this app, so /api is same-origin.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Online seat ticket: one saved entry { code, ticket, game_id } (spec "Browser storage").
const ONLINE_KEY = "porosity_online";

export function getOnline() {
  try {
    const v = JSON.parse(localStorage.getItem(ONLINE_KEY) || "null");
    return v && v.code && v.ticket ? v : null;
  } catch {
    return null;
  }
}

export function saveOnline({ code, ticket, game_id = null }) {
  localStorage.setItem(ONLINE_KEY, JSON.stringify({ code, ticket, game_id }));
}

export function clearOnline() {
  localStorage.removeItem(ONLINE_KEY);
}

api.interceptors.request.use((config) => {
  const ticket = getOnline()?.ticket;
  if (ticket) config.headers["X-Player-Ticket"] = ticket;
  config.metadata = { sentAt: Date.now() };
  return config;
});

// Server clock: every reply carries X-Server-Time (ms). Prompt countdowns
// (Protection Card) run on serverNow() so a device clock that is off can't
// make an answer look in time when the server's deadline has already passed.
let clockOffsetMs = 0;
let bestRoundTrip = Infinity;
function syncClock(res) {
  const server = Number(res?.headers?.["x-server-time"]);
  const sentAt = res?.config?.metadata?.sentAt;
  if (!Number.isFinite(server) || !sentAt) return;
  const now = Date.now();
  const rtt = now - sentAt;
  // Keep the estimate from the fastest round trip (least network noise), but
  // refresh it now and then so a drifting device clock is followed.
  if (rtt <= bestRoundTrip * 1.5 || Math.random() < 0.05) {
    bestRoundTrip = Math.min(bestRoundTrip, rtt);
    clockOffsetMs = server - (sentAt + rtt / 2);
  }
}
export const serverNow = () => Date.now() + clockOffsetMs;

api.interceptors.response.use(
  (res) => {
    syncClock(res);
    return res;
  },
  (err) => {
    if (err?.response) syncClock(err.response);
    return Promise.reject(err);
  },
);

// Server error text: 401 bodies are { message, lobby_code }, others a plain string.
export function errorText(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d.message === "string") return d.message;
  return fallback;
}

export async function createLobby(name, color) {
  const { data } = await api.post("/lobbies", { name, color });
  return data;
}

export async function getLobby(code) {
  const { data } = await api.get(`/lobbies/${code}`);
  return data;
}

export async function joinLobby(code, name, color) {
  const { data } = await api.post(`/lobbies/${code}/join`, { name, color });
  return data;
}

export async function leaveLobby(code) {
  const { data } = await api.post(`/lobbies/${code}/leave`);
  return data;
}

export async function startLobby(code) {
  const { data } = await api.post(`/lobbies/${code}/start`);
  return data;
}

export async function reclaimSeat(code, seat) {
  const { data } = await api.post(`/lobbies/${code}/reclaim`, { seat });
  return data;
}

export async function createGame(players) {
  const { data } = await api.post("/games", { players });
  return data;
}

export async function getGame(gameId, since = null) {
  const { data } = await api.get(`/games/${gameId}`, since === null ? undefined : { params: { since } });
  return data;
}

export async function skipGoneSeat(gameId) {
  const { data } = await api.post(`/games/${gameId}/skip`);
  return data;
}

export async function placeStartingCube(gameId, cell) {
  const { data } = await api.post(
    `/games/${gameId}/actions/place_starting_cube`,
    { cell },
  );
  return data;
}

export async function skipToRound(gameId, round) {
  const { data } = await api.post(
    `/games/${gameId}/actions/skip_to_round`,
    { round },
  );
  return data;
}

export async function randomizeSetup(gameId) {
  const { data } = await api.post(
    `/games/${gameId}/actions/randomize_setup`,
  );
  return data;
}

export async function playBuild(gameId, cardIndex, cell, edge = null) {
  const { data } = await api.post(`/games/${gameId}/actions/play_build`, {
    card_index: cardIndex,
    cell,
    ...(edge ? { edge } : {}),
  });
  return data;
}

export async function playAction(gameId, cardIndex) {
  const { data } = await api.post(`/games/${gameId}/actions/play_action`, {
    card_index: cardIndex,
  });
  return data;
}

export async function discardCard(gameId, cardIndex) {
  const { data } = await api.post(`/games/${gameId}/actions/discard`, {
    card_index: cardIndex,
  });
  return data;
}

// TURN STRUCTURE v2
export async function endTurn(gameId) {
  const { data } = await api.post(`/games/${gameId}/actions/end_turn`);
  return data;
}

export async function takeActionCard(gameId) {
  const { data } = await api.post(`/games/${gameId}/actions/take_action_card`);
  return data;
}

// Pick: the top build deck card, face down, instead of a market card.
export async function drawDeck(gameId) {
  const { data } = await api.post(`/games/${gameId}/actions/draw_deck`);
  return data;
}

// PAY UPFRONT: pay for the blind-drawn card shown to you (it joins your hand).
export async function payBlind(gameId) {
  const { data } = await api.post(`/games/${gameId}/actions/pay_blind`);
  return data;
}

// PAY UPFRONT: skip the take after a play / discard (only when no market card is affordable).
export async function skipPick(gameId) {
  const { data } = await api.post(`/games/${gameId}/actions/skip_pick`);
  return data;
}

// Discard one held action card (free, no replacement, limited per game).
export async function discardActionCard(gameId, cardIndex) {
  const { data } = await api.post(`/games/${gameId}/actions/discard_action`, { card_index: cardIndex });
  return data;
}

export async function wipeMarket(gameId) {
  const { data } = await api.post(`/games/${gameId}/actions/wipe_market`);
  return data;
}

export async function drawCard(gameId, source, marketIndex = null) {
  const { data } = await api.post(`/games/${gameId}/actions/draw`, {
    source,
    market_index: marketIndex,
  });
  return data;
}

// Testing Mode only: the result {face, cells?: ["r,c", ...], global_no?} of the next automatic weather draw
// (null clears it). The server refuses it outside Testing Mode.
export async function weatherNextSpin(gameId, force = null) {
  const { data } = await api.post(`/games/${gameId}/weather/next_spin`, force || {});
  return data;
}

export async function setTestingMode(gameId, enabled) {
  const { data } = await api.post(`/games/${gameId}/testing_mode`, { enabled });
  return data;
}

export async function weatherProtectionDecide(gameId, play) {
  const { data } = await api.post(
    `/games/${gameId}/weather/protection_decide`,
    { play },
  );
  return data;
}

export async function demolishPreview(gameId, cell) {
  const { data } = await api.get(
    `/games/${gameId}/actions/demolish_preview`,
    { params: { cell } },
  );
  return data;
}

export async function demolish(gameId, cell) {
  const { data } = await api.post(`/games/${gameId}/actions/demolish`, {
    cell,
  });
  return data;
}

export async function getConnectivity(gameId) {
  const { data } = await api.get(`/games/${gameId}/connectivity`);
  return data;
}

export async function getScoring(gameId) {
  const { data } = await api.get(`/games/${gameId}/scoring`);
  return data;
}

export async function playAgain(gameId) {
  const { data } = await api.post(`/games/${gameId}/play_again`);
  return data;
}

export async function getSettings() {
  const { data } = await api.get(`/settings`);
  return data;
}

export async function patchSettings(patch) {
  const { data } = await api.patch(`/settings`, { patch });
  return data;
}

export async function patchGameSettings(gameId, patch) {
  const { data } = await api.post(`/games/${gameId}/settings`, { patch });
  return data;
}

export async function actionCardSelectCell(gameId, cell, edge = null) {
  const { data } = await api.post(
    `/games/${gameId}/actions/cards/select_cell`,
    edge ? { cell, edge } : { cell },
  );
  return data;
}

export async function actionCardBack(gameId) {
  const { data } = await api.post(`/games/${gameId}/actions/cards/back`);
  return data;
}

export async function actionCardConfirm(gameId) {
  const { data } = await api.post(`/games/${gameId}/actions/cards/confirm`);
  return data;
}

export async function actionCardCancel(gameId) {
  const { data } = await api.post(`/games/${gameId}/actions/cards/cancel`);
  return data;
}

// PRIVATE RESPONSE WINDOW (Board Thief block / Mangrove prompt): answer for yourself.
export async function responsesDecide(gameId, yes, itemId = null) {
  const { data } = await api.post(`/games/${gameId}/responses/decide`, { yes, item_id: itemId });
  return data;
}

// CLIMATE BANK (v3 step 5): pay coins into the shared bank, propose a project (opens the vote:
// answer it with responsesDecide(gameId, yes, "vote")).
export async function bankPay(gameId, amount) {
  const { data } = await api.post(`/games/${gameId}/bank/pay`, { amount });
  return data;
}

export async function bankPropose(gameId, project) {
  const { data } = await api.post(`/games/${gameId}/bank/propose`, { project });
  return data;
}

export async function getWeatherFaces() {
  const { data } = await api.get(`/weather/faces`);
  return data;
}

// Player identity — saved colour keys shown as clay / sand / plum / rose (never blue or green).
export const PLAYER_COLORS = {
  red: "#B4634A",
  yellow: "#D2B064",
  purple: "#5E4F7A",
  orange: "#C9879A",
};

// Building shades per colour key: roof (light) / wall (base) / edge (dark).
export const PLAYER_SHADES = {
  red: { roof: "#D59C88", wall: "#B4634A", edge: "#8C4732" },
  yellow: { roof: "#E9D49E", wall: "#D2B064", edge: "#A88A42" },
  purple: { roof: "#978BAF", wall: "#5E4F7A", edge: "#43375C" },
  orange: { roof: "#E3B6C2", wall: "#C9879A", edge: "#9C5D71" },
};

// Text colour that reads on each player swatch.
export const PLAYER_ON_COLOR = { red: "#FFFFFF", yellow: "var(--ink)", purple: "#FFFFFF", orange: "var(--ink)" };

export const PLAYER_BORDER_COLORS = { ...PLAYER_COLORS };

export const PLAYER_COLOR_ORDER = ["red", "yellow", "purple", "orange"];

export const PLAYER_COLOR_NAMES = { red: "Clay", yellow: "Sand", purple: "Plum", orange: "Rose" };

// Infrastructure category → flat fallback fill (used for tiles/tokens
// without a dedicated inline illustration).
export const CATEGORY_FILLS = {
  gray: "#7a7469",
  green: "#3e6b3e",
  blue: "#356580",
};

export const CATEGORY_BADGE_COLOR = {
  gray: "#7d7870",
  green: "#466b46",
  blue: "#3a6480",
};

// card_key → infrastructure category (mirrors backend classification).
const GRAY_KEYS = new Set(["low_sea_wall"]);
const GREEN_KEYS = new Set(["urban_park", "urban_forest", "green_roof", "green_street"]);
const BLUE_KEYS = new Set([
  "mangrove_forest",
  "inland_wetland",
]);

export function categoryOfCardKey(cardKey) {
  if (!cardKey) return "gray";
  if (cardKey === "house_cube") return "gray";
  if (GRAY_KEYS.has(cardKey)) return "gray";
  if (GREEN_KEYS.has(cardKey)) return "green";
  if (BLUE_KEYS.has(cardKey)) return "blue";
  return "gray";
}

export async function getBuildPreview(gameId, cardIndex) {
  const { data } = await api.get(`/games/${gameId}/build_preview`, { params: { card_index: cardIndex } });
  return data;
}

// Feedback form (all fields but the message are optional) and the private inbox.
export async function sendFeedback(entry) {
  const { data } = await api.post("/feedback", entry);
  return data;
}

export async function listFeedback(key) {
  const { data } = await api.get("/feedback", { headers: { "X-Feedback-Key": key } });
  return data;
}
